import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, AlertCircle } from 'lucide-react';

/**
 * Marketing Footer Component
 * Includes brand info, navigation links, support resources, legal links, and crisis resources
 */
const MarketingFooter = () => {
  const programLinks = [
    { name: 'How It Works', path: '/how-it-works' },
    { name: 'Benefits', path: '/benefits' },
    { name: 'The Journey', path: '/journey' },
      { name: 'Pricing', path: '/pricing' },
    { name: 'For Clinicians', path: '/clinicians' },
    { name: 'FAQ', path: '/faq' },
    { name: 'Research References', path: '/research-references' },
  ];

  const supportLinks = [
    { name: 'Contact Us', path: '/contact' },
    { name: 'Crisis Resources', path: '/crisis-resources', icon: AlertCircle },
  ];

  const legalLinks = [
    { name: 'Privacy Policy', path: '/privacy' },
    { name: 'Terms of Service', path: '/terms' },
    { name: 'HIPAA Notice', path: '/hipaa-notice' },
  ];

  return (
    <footer className="text-white" style={{ backgroundColor: 'var(--brand-green-darker)' }}>
      {/* Main Footer Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16" style={{ maxWidth: '1280px' }}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <div className="mb-4">
              <h3 className="text-xl font-bold text-white">MHDF Momentum</h3>
              <p className="text-sm text-neutral-400 mt-1">365 Days of Mental Wellness Companionship</p>
            </div>
            <p className="text-sm text-neutral-300 leading-relaxed">
              A clinical supplement for daily mental wellness companionship. Structured daily support between therapeutic sessions.
            </p>
            <div className="mt-6 space-y-2">
              <div className="flex items-center gap-2 text-sm text-neutral-300">
                <Phone size={16} />
                <span>(409) 550-1846</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-neutral-300">
                <Mail size={16} />
                <span>support@mhdfmomentum.com</span>
              </div>
            </div>
          </div>

          {/* Program Links */}
          <div>
            <h4 className="text-sm font-semibold text-white uppercase tracking-wide mb-4">
              Program
            </h4>
            <ul className="space-y-3">
              {programLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-neutral-300 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h4 className="text-sm font-semibold text-white uppercase tracking-wide mb-4">
              Support
            </h4>
            <ul className="space-y-3">
              {supportLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-neutral-300 hover:text-white transition-colors inline-flex items-center gap-2"
                  >
                    {link.icon && <link.icon size={16} />}
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h4 className="text-sm font-semibold text-white uppercase tracking-wide mb-4">
              Legal
            </h4>
            <ul className="space-y-3">
              {legalLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sm text-neutral-300 hover:text-white transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-neutral-400">
              © 2026 MHDF Technologies LLC. All rights reserved.
            </p>
            <div className="flex items-center gap-4 text-xs text-neutral-400">
              <span className="px-2 py-1 bg-white/5 rounded border border-white/10">
                HIPAA Compliant
              </span>
              <span className="px-2 py-1 bg-white/5 rounded border border-white/10">
                Evidence-Based
              </span>
              <span className="px-2 py-1 bg-white/5 rounded border border-white/10">AES-256 Encrypted</span>
            </div>
          </div>
          
          <div className="mt-4 text-center md:text-left">
            <p className="text-xs text-neutral-500">
              Created by MHDF Technologies LLC. Managed by Momentum Transformational Coaching LLC.
            </p>
            <p className="mt-2 text-xs text-neutral-500">
              This is not a substitute for professional mental health services.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default MarketingFooter;
